import { generateHTML } from "@tiptap/html"
import StarterKit from "@tiptap/starter-kit"
import cn from "classnames"
import { useAtom } from "jotai"
import { camelCase } from "lodash"
import { useFormContext } from "react-hook-form"

import FaIcon from "components/FontAwesomeIcon"
import { formatAmountForDisplay } from "utils/stripe"

import ProductImages from "../ProductImages"

import {
  currentStepAtom,
  priceAtom,
  selectedVariantAddonsAtom,
  setStepsAtom
} from "./index"

import type { ProductTypeBookingForm } from "./index"
import type { ProductVariantWithImagesAndAddons } from "pages/products/[slug]"
import type { UseFormReturn } from "react-hook-form"

const Variants: React.FC<{
  variants: ProductVariantWithImagesAndAddons[]
  step?: number
  parentTitle?: string
}> = ({ variants }) => {
  const rhf = useFormContext() as UseFormReturn<ProductTypeBookingForm>
  const [currentStep] = useAtom(currentStepAtom)
  const [, setSteps] = useAtom(setStepsAtom)
  const [, setPrice] = useAtom(priceAtom)
  const [, setSelectedVariantAddonsAtom] = useAtom(selectedVariantAddonsAtom)
  const prevStep = rhf.watch(`variant.${currentStep - 1}`)
  const variantsWithNoParent = variants.filter(
    variant => variant.parentVariantId === null
  )

  const variantsToShow =
    currentStep === 0
      ? variantsWithNoParent
      : variants.filter(variant => variant.parentVariantId === prevStep?.id)

  return (
    <>
      {variantsToShow.map((variant, variantIndex) => {
        const isSelected = rhf.watch(`variant.${currentStep}.id`) === variant.id
        const variantClass = cn(
          "bg-gray-200 p-3 my-3 cursor-pointer hover:scale-105 transition-transform",
          {
            "ring-2 ring-primary": isSelected
          }
        )

        return (
          <div
            key={`variant${camelCase(variant.title)}-${variantIndex}`}
            id={`variant${camelCase(variant.title)}-${variantIndex}`}
          >
            <div
              className={variantClass}
              onClick={e => {
                const currentValue = rhf.getValues("variant")
                rhf.setValue(
                  "variant",
                  {
                    ...currentValue,
                    // store the title for the summary, and the id for the add to cart
                    [currentStep]: {
                      title: variant.title,
                      id: variant.id
                    }
                  },
                  { shouldDirty: true }
                )

                setPrice(prev => ({
                  ...prev,
                  variant: variant?.price || 0
                }))

                setSelectedVariantAddonsAtom(variant?.addons || [])

                setSteps({
                  event: e,
                  stepTo: currentStep + 1,
                  rhf
                })
                setTimeout(() => {
                  window.scrollTo(0, 0)
                }, 0)
              }}
            >
              <div className="flex items-center justify-between gap-6 min-h-[150px]">
                <div className="flex-1">
                  <h3>{variant.title}</h3>
                  {variant.price && (
                    <p className="font-bold">
                      {formatAmountForDisplay(variant.price, "gbp")}
                    </p>
                  )}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: generateHTML(JSON.parse(variant.content || ""), [
                        StarterKit
                      ])
                    }}
                  />
                </div>
                {variant.images && variant.images.length > 0 ? (
                  <div className="w-1/3 relative grid aspect-square">
                    <ProductImages
                      images={variant.images}
                      className="max-w-full w-full min-w-0 min-h-0"
                    />
                  </div>
                ) : (
                  <FaIcon
                    icon="chevron-right"
                    size="2x"
                    className="text-white"
                  />
                )}
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}

export default Variants
